/**
 * Serviceability check utility.
 * Uses the Haversine formula to determine if coordinates fall within
 * the delivery radius of the store location (loaded from Firestore settings).
 */

import { getStoreCoordinates, getDeliveryRadius } from './deliverySlots';
import { getCachedSettings } from '../services/settingsService';

const toRad = (degrees) => degrees * (Math.PI / 180);

/**
 * Get the effective store config — tries deliverySlots first,
 * falls back to cached Firestore settings.
 */
const getEffectiveStoreConfig = () => {
  let coords = getStoreCoordinates();
  let radius = getDeliveryRadius();

  console.log('[Serviceability] From deliverySlots → coords:', coords, 'radius:', radius);

  // If deliverySlots module hasn't been updated yet, try cached settings directly
  if (!coords.latitude || !coords.longitude || !radius) {
    const cached = getCachedSettings();
    console.log('[Serviceability] Fallback → cachedSettings:', cached);
    if (cached) {
      coords = {
        latitude: Number(cached.latitude) || null,
        longitude: Number(cached.longitude) || null,
      };
      radius = Number(cached.deliveryRadius) || null;
      console.log('[Serviceability] Using cached → coords:', coords, 'radius:', radius);
    }
  }

  return { coords, radius };
};

/**
 * Check if coordinates are within the serviceable radius.
 * @param {number} latitude
 * @param {number} longitude
 * @returns {boolean}
 */
export const isCoordinateServiceable = (latitude, longitude) => {
  console.log('[Serviceability] === CHECK START ===');
  console.log('[Serviceability] Customer location:', { latitude, longitude });

  const { coords: storeCoords, radius: maxRadius } = getEffectiveStoreConfig();

  console.log('[Serviceability] Final store coords:', storeCoords);
  console.log('[Serviceability] Final radius:', maxRadius);

  // If settings truly haven't loaded yet, allow (will be validated at checkout)
  if (!storeCoords.latitude || !storeCoords.longitude || !maxRadius) {
    console.warn('[Serviceability] ⚠️ Settings NOT available — returning true by default');
    return true;
  }

  const R = 6371; // Earth's radius in km
  const dLat = toRad(latitude - storeCoords.latitude);
  const dLon = toRad(longitude - storeCoords.longitude);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(storeCoords.latitude)) *
      Math.cos(toRad(latitude)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  const result = distance <= maxRadius;
  console.log('[Serviceability] Distance:', distance.toFixed(3), 'km | Max allowed:', maxRadius, 'km | Serviceable:', result);
  console.log('[Serviceability] === CHECK END ===');

  return result;
};

/**
 * Check if an address object is serviceable.
 * @param {Object} address
 * @returns {boolean}
 */
export const isAddressServiceable = (address) => {
  if (!address) return false;
  const lat = parseFloat(address.latitude);
  const lng = parseFloat(address.longitude);
  if (isNaN(lat) || isNaN(lng)) return false;
  return isCoordinateServiceable(lat, lng);
};
